import {
    Box,
    Typography,
    Card,
    CardContent,
    Grid,
    TextField,
    Button,
} from "@mui/material"
import {Edit as EditIcon, Save as SaveIcon, Cancel as CancelIcon} from "@mui/icons-material"
import {useTranslation} from "react-i18next";
import LogoUploader from "../components/common/logo-uploader";
import CompanyOperations from "./company-operations";

export default function CompanyProfileSettings({
                                               tabValue,
                                               tabIndex = 5,
                                               tabPrefix = "distributor",
                                               nameLabel = "CompanyName",
                                               tenantId,
                                               distributor,
                                               logoUrl,
                                               onLogoUpdate,
                                               onWorkingHoursUpdate,
                                               editMode,
                                               saving = false,
                                               errors = {},
                                               tenantType,
                                               editedDistributor,
                                               onEditToggle,
                                               onSave,
                                               onInputChange,
                                               onAddressChange,
                                           }) {
    const {t} = useTranslation();
    const feedback = field => ({error: !!errors[field], helperText: errors[field] ? t(errors[field]) : undefined});

    return (
        <div role="tabpanel" hidden={tabValue !== tabIndex} id={`${tabPrefix}-tabpanel-${tabIndex}`} aria-labelledby={`${tabPrefix}-tab-${tabIndex}`}>
            {tabValue === tabIndex && (
                <Box sx={{"& .MuiCard-root": {boxShadow: "none", border: 1, borderColor: "divider", borderRadius: 2}}}>
                    <Box sx={{display: "flex", flexWrap: "wrap", gap: 2, justifyContent: "space-between", alignItems: "center", mb: 3}}>
                        <Typography variant="h6">{t("WorkspaceCompanyProfile")}</Typography>
                        <Box sx={{display: "flex", flexWrap: "wrap", gap: 1}}>
                            {editMode ? (
                                <>
                                    <Button variant="outlined" disabled={saving} startIcon={<CancelIcon/>} onClick={onEditToggle}
                                            sx={{mr: 1}}>
                                        {t("Cancel")}
                                    </Button>
                                    <Button variant="contained" disabled={saving} startIcon={<SaveIcon/>} onClick={onSave}>
                                        {t(saving ? "ProfileSaving" : "SaveChanges")}
                                    </Button>
                                </>
                            ) : (
                                <Button variant="contained" startIcon={<EditIcon/>} onClick={onEditToggle}>
                                    {t("EditProfile")}
                                </Button>
                            )}
                        </Box>
                    </Box>

                    <Card sx={{mb: 3}}><CardContent>
                        <Box sx={{display: "flex", flexWrap: "wrap", alignItems: "center", gap: 2}}>
                            <LogoUploader tenantId={tenantId} logoUrl={logoUrl} hasLogo={!!distributor?.imageUrl}
                                onUpdate={onLogoUpdate} size={64}/>
                            <Box sx={{minWidth: 0, flex: 1}}><Typography variant="subtitle1">{t("WorkspaceCompanyLogo")}</Typography>
                                <Typography variant="body2" color="text.secondary">{t("WorkspaceLogoHelp")}</Typography></Box>
                        </Box>
                    </CardContent></Card>

                    <Grid container spacing={3}>
                        {/* Basic Information */}
                        <Grid item xs={12}>
                            <Card>
                                <CardContent>
                                    <Typography variant="h6" gutterBottom>
                                        {t("BasicInformation")}
                                    </Typography>
                                    <Grid container spacing={3}>
                                        <Grid item xs={12} md={6}>
                                            <TextField
                                                fullWidth
                                                label={t(nameLabel)}
                                                required {...feedback("tenantNameEn")}
                                                value={editedDistributor?.tenantNameEn || ""}
                                                onChange={(e) => onInputChange("tenantNameEn", e.target.value)}
                                                disabled={!editMode || saving}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            <TextField
                                                fullWidth
                                                label={t("Email")}
                                                type="email" required {...feedback("tenantEmail")}
                                                value={editedDistributor?.tenantEmail || ""}
                                                onChange={(e) => onInputChange("tenantEmail", e.target.value)}
                                                disabled={!editMode || saving}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            <TextField
                                                fullWidth
                                                label={t("Website")}
                                                {...feedback("website")}
                                                value={editedDistributor?.website || ""}
                                                onChange={(e) => onInputChange("website", e.target.value)}
                                                disabled={!editMode || saving}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            <TextField
                                                fullWidth
                                                label={t("LicenseNumber")}
                                                value={editedDistributor?.registrationNumber || ""}
                                                onChange={(e) => onInputChange("registrationNumber", e.target.value)}
                                                disabled={!editMode || saving}
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                label={t("Description")}
                                                multiline
                                                rows={4}
                                                value={editedDistributor?.about || ""}
                                                onChange={(e) => onInputChange("about", e.target.value)}
                                                disabled={!editMode || saving}
                                            />
                                        </Grid>
                                    </Grid>
                                </CardContent>
                            </Card>
                        </Grid>

                        {/* Address Information */}
                        <Grid item xs={12}>
                            <Card>
                                <CardContent>
                                    <Typography variant="h6" gutterBottom>
                                        {t("AddressInformation")}
                                    </Typography>
                                    <Grid container spacing={3}>
                                        <Grid item xs={12} md={4}>
                                            <TextField
                                                fullWidth
                                                label={t("Street")}
                                                required {...feedback("street")}
                                                value={editedDistributor?.address?.street || ""}
                                                onChange={(e) => onAddressChange("street", e.target.value)}
                                                disabled={!editMode || saving}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={4}>
                                            <TextField
                                                fullWidth
                                                label={t("City")}
                                                required {...feedback("city")}
                                                value={editedDistributor?.address?.city || ""}
                                                onChange={(e) => onAddressChange("city", e.target.value)}
                                                disabled={!editMode || saving}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={4}>
                                            <TextField
                                                fullWidth
                                                label={t("Country")}
                                                required {...feedback("country")}
                                                value={editedDistributor?.address?.country || ""}
                                                onChange={(e) => onAddressChange("country", e.target.value)}
                                                disabled={!editMode || saving}
                                            />
                                        </Grid>
                                    </Grid>
                                </CardContent>
                            </Card>
                        </Grid>

                        {/* Phone Numbers */}
                        <Grid item xs={12}>
                            <Card>
                                <CardContent>
                                    <Typography variant="h6" gutterBottom>
                                        {t("PhoneNumbers")}
                                    </Typography>
                                    {editedDistributor?.phones?.map((phone, index) => (
                                        <Grid container spacing={2} key={index} sx={{mb: 2}}>
                                            <Grid item xs={12} md={4}>
                                                <TextField
                                                    fullWidth
                                                    label={t("CountryCode")}
                                                    {...feedback(`phoneCode${index}`)}
                                                    value={phone.phoneNumberCode || ""}
                                                    onChange={(e) => {
                                                        const newPhones = [...editedDistributor.phones]
                                                        newPhones[index] = {...newPhones[index], phoneNumberCode: e.target.value}
                                                        onInputChange("phones", newPhones)
                                                    }}
                                                    disabled={!editMode || saving}
                                                    placeholder="+374"
                                                />
                                            </Grid>
                                            <Grid item xs={12} md={6}>
                                                <TextField
                                                    fullWidth
                                                    label={t("PhoneNumber")}
                                                    {...feedback(`phone${index}`)}
                                                    value={phone.phoneNumber || ""}
                                                    onChange={(e) => {
                                                        const newPhones = [...editedDistributor.phones]
                                                        newPhones[index] = {...newPhones[index], phoneNumber: e.target.value}
                                                        onInputChange("phones", newPhones)
                                                    }}
                                                    disabled={!editMode || saving}
                                                    placeholder="10-123456"
                                                />
                                            </Grid>
                                            <Grid item xs={12} md={2}>
                                                {editMode && (
                                                    <Button
                                                        variant="outlined"
                                                        color="error"
                                                        onClick={() => {
                                                            const newPhones = editedDistributor.phones.filter((_, i) => i !== index)
                                                            onInputChange("phones", newPhones)
                                                        }}
                                                        disabled={saving || editedDistributor.phones.length <= 1}
                                                        sx={{height: "56px"}}
                                                    >
                                                        {t("Remove")}
                                                    </Button>
                                                )}
                                            </Grid>
                                        </Grid>
                                    ))}
                                    {editMode && (
                                        <Button
                                            variant="outlined"
                                            disabled={saving}
                                            onClick={() => {
                                                const newPhones = [
                                                    ...(editedDistributor.phones || []),
                                                    {phoneNumberCode: "+374", phoneNumber: ""},
                                                ]
                                                onInputChange("phones", newPhones)
                                            }}
                                            sx={{mt: 1}}
                                        >
                                            {t("AddPhoneNumber")}
                                        </Button>
                                    )}
                                </CardContent>
                            </Card>
                        </Grid>

                        {/* Business Information */}
                        <Grid item xs={12}>
                            <Card>
                                <CardContent>
                                    <Typography variant="h6" gutterBottom>
                                        {t("BusinessInformation")}
                                    </Typography>
                                    <Grid container spacing={3}>
                                        <Grid item xs={12} md={6}>
                                            <TextField
                                                fullWidth
                                                label={t("Founded")}
                                                {...feedback("foundedYear")}
                                                type="number"
                                                value={editedDistributor?.foundedYear || ""}
                                                onChange={(e) => onInputChange("foundedYear", e.target.value)}
                                                disabled={!editMode || saving}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            <TextField
                                                fullWidth
                                                label={t("Category")}
                                                value={tenantType === "IMPORTER" ? t("Distributor") : tenantType === "PHARMACY" ? t("Pharmacy") : t(tenantType || "")}
                                                slotProps={{input: {readOnly: true}}}
                                                helperText={t("ProfileTypeReadOnly")}
                                            />
                                        </Grid>
                                        {tenantType === "PHARMACY" && <Grid item xs={12}>
                                            <TextField fullWidth label={t("WorkspaceTaxpayerNumber")} value={distributor?.individualTaxpayerNumber || ""}
                                                slotProps={{input: {readOnly: true}}}/>
                                        </Grid>}
                                    </Grid>
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>
                    {distributor && <CompanyOperations distributor={distributor} onWorkingHoursUpdate={onWorkingHoursUpdate}/>}
                </Box>
            )}
        </div>
    )
}
